// Sliding Window — Basic vs Optimized code for every "Beginner Problems" drill listed in
// the Sliding Window Explanation modal. Same shape as twoPointersDrills.js: concise steps +
// an interactive "watch it happen" visualizer per version. Two Advanced drills cross-reference
// the fuller curated problems instead of duplicating content, mirroring arraysDrills.js's
// Kadane's stub.

const slidingWindowDrills = [
  {
    id: 'max-sum-subarray-k',
    title: 'Maximum Sum Subarray of Size K',
    level: 'Easy',
    versions: [
      {
        name: 'Basic — Sum Every Window From Scratch',
        explanation: 'For each possible window start, add up all k elements to get that window\'s sum. Correct, but every window re-adds k elements even though most of them were already counted in the previous window.',
        steps: [
          'For each start index from 0 to n − k, sum the k elements beginning there.',
          'Track the maximum sum seen across all windows.',
          'Each window costs O(k) to sum, even though it overlaps k − 1 elements with the previous window.',
          'Total cost: O(n · k).',
        ],
        visual: {
          arrays: [{ label: 'arr (k = 3)', values: [2, 1, 5, 1, 3, 2] }],
          varNames: ['max'],
          steps: [
            { highlights: { arr: { primary: [0, 1, 2] } }, vars: { max: 8 }, note: 'Window [0,2]: 2+1+5 = 8. max = 8.' },
            { highlights: { arr: { primary: [1, 2, 3] } }, vars: { max: 8 }, note: 'Window [1,3]: 1+5+1 = 7. Not better.' },
            { highlights: { arr: { primary: [2, 3, 4] } }, vars: { max: 9 }, note: 'Window [2,4]: 5+1+3 = 9. New max!' },
            { highlights: { arr: { primary: [3, 4, 5] } }, vars: { max: 9 }, note: 'Window [3,5]: 1+3+2 = 6. Not better. Every window resummed all 3 elements from scratch. Final max: 9.' },
          ],
        },
        code:
`public int maxSumSubarrayBasic(int[] arr, int k) {
    int max = Integer.MIN_VALUE;
    for (int start = 0; start <= arr.length - k; start++) {
        int sum = 0;
        for (int i = start; i < start + k; i++) {
            sum += arr[i];
        }
        max = Math.max(max, sum);
    }
    return max;
}`,
        complexity: { time: 'O(n · k)', space: 'O(1)' },
      },
      {
        name: 'Optimized — Slide the Sum in O(1) Per Step',
        explanation: 'Compute the first window\'s sum once, then for every slide, just subtract the element leaving on the left and add the element entering on the right. Every window after the first costs O(1) instead of O(k).',
        steps: [
          'Sum the first k elements to get the initial window sum.',
          'For each subsequent window, subtract the outgoing element (leaving the left) and add the incoming element (entering the right).',
          'Track the maximum sum after each slide.',
          'Each slide is O(1) — total cost O(n), not O(n · k).',
        ],
        visual: {
          arrays: [{ label: 'arr (k = 3)', values: [2, 1, 5, 1, 3, 2] }],
          varNames: ['sum', 'max'],
          steps: [
            { highlights: { arr: { primary: [0, 1, 2] } }, vars: { sum: 8, max: 8 }, note: 'Initial window [0,2]: sum = 2+1+5 = 8.' },
            { highlights: { arr: { primary: [1, 2, 3] } }, vars: { sum: 7, max: 8 }, note: 'Slide: subtract arr[0]=2, add arr[3]=1. sum = 8-2+1 = 7. Not better.' },
            { highlights: { arr: { primary: [2, 3, 4] } }, vars: { sum: 9, max: 9 }, note: 'Slide: subtract arr[1]=1, add arr[4]=3. sum = 7-1+3 = 9. New max!' },
            { highlights: { arr: { primary: [3, 4, 5] } }, vars: { sum: 6, max: 9 }, note: 'Slide: subtract arr[2]=5, add arr[5]=2. sum = 9-5+2 = 6. Not better. Final max: 9 — same answer, no window ever resummed.' },
          ],
        },
        code:
`public int maxSumSubarray(int[] arr, int k) {
    int sum = 0;
    for (int i = 0; i < k; i++) sum += arr[i];
    int max = sum;

    for (int right = k; right < arr.length; right++) {
        int left = right - k;
        sum = sum - arr[left] + arr[right];
        max = Math.max(max, sum);
    }
    return max;
}`,
        complexity: { time: 'O(n)', space: 'O(1)' },
      },
    ],
  },
  {
    id: 'average-subarrays-k',
    title: 'Average of All Subarrays of Size K',
    level: 'Easy',
    versions: [
      {
        name: 'Basic — Sum Every Window From Scratch',
        explanation: 'For each window, add up its k elements and divide by k. The same overlap-blindness as the max-sum version: every window recomputes a sum that mostly duplicates the previous one.',
        steps: [
          'For each start index, sum the k elements beginning there.',
          'Divide that sum by k to get the average.',
          'Repeat for every window — O(k) work per window, O(n · k) total.',
        ],
        visual: {
          arrays: [{ label: 'arr (k = 3)', values: [3, 9, 6, 0, 3] }],
          varNames: [],
          steps: [
            { highlights: { arr: { primary: [0, 1, 2] } }, note: 'Window [0,2]: (3+9+6)/3 = 6.0.' },
            { highlights: { arr: { primary: [1, 2, 3] } }, note: 'Window [1,3]: (9+6+0)/3 = 5.0.' },
            { highlights: { arr: { primary: [2, 3, 4] } }, note: 'Window [2,4]: (6+0+3)/3 = 3.0. Each average recomputed its sum from nothing.' },
          ],
        },
        code:
`public double[] averagesBasic(int[] arr, int k) {
    double[] result = new double[arr.length - k + 1];
    for (int start = 0; start <= arr.length - k; start++) {
        int sum = 0;
        for (int i = start; i < start + k; i++) sum += arr[i];
        result[start] = (double) sum / k;
    }
    return result;
}`,
        complexity: { time: 'O(n · k)', space: 'O(n) — for the result array' },
      },
      {
        name: 'Optimized — Slide the Sum, Divide Once Per Window',
        explanation: 'Reuse the fixed-window O(1) slide from the max-sum version — the only difference here is dividing by k at the end of each step instead of comparing to a running max.',
        steps: [
          'Sum the first k elements for the initial window.',
          'Divide by k to record the first average.',
          'For each slide, subtract the outgoing element, add the incoming element, then divide by k again.',
          'Every window after the first costs O(1) instead of O(k).',
        ],
        visual: {
          arrays: [{ label: 'arr (k = 3)', values: [3, 9, 6, 0, 3] }],
          varNames: ['sum'],
          steps: [
            { highlights: { arr: { primary: [0, 1, 2] } }, vars: { sum: 18 }, note: 'Initial window: sum = 3+9+6 = 18. Average = 18/3 = 6.0.' },
            { highlights: { arr: { primary: [1, 2, 3] } }, vars: { sum: 15 }, note: 'Slide: subtract arr[0]=3, add arr[3]=0. sum = 18-3+0 = 15. Average = 5.0.' },
            { highlights: { arr: { primary: [2, 3, 4] } }, vars: { sum: 9 }, note: 'Slide: subtract arr[1]=9, add arr[4]=3. sum = 15-9+3 = 9. Average = 3.0. Same results, no window resummed.' },
          ],
        },
        code:
`public double[] averages(int[] arr, int k) {
    double[] result = new double[arr.length - k + 1];
    int sum = 0;
    for (int i = 0; i < k; i++) sum += arr[i];
    result[0] = (double) sum / k;

    for (int right = k; right < arr.length; right++) {
        int left = right - k;
        sum = sum - arr[left] + arr[right];
        result[right - k + 1] = (double) sum / k;
    }
    return result;
}`,
        complexity: { time: 'O(n)', space: 'O(n) — for the result array' },
      },
    ],
  },
  {
    id: 'first-negative-in-window',
    title: 'First Negative Number in Every Window of Size K',
    level: 'Easy',
    versions: [
      {
        name: 'Basic — Rescan Each Window for a Negative',
        explanation: 'For every window, scan its k elements from the start looking for the first negative number. Correct, but every window rescans elements the previous window already looked at.',
        steps: [
          'For each window, scan from its start looking for the first negative value.',
          'Record that value (or 0 if the window has no negatives).',
          'Repeat for every window — O(k) work per window in the worst case, O(n · k) total.',
        ],
        visual: {
          arrays: [{ label: 'arr (k = 3)', values: [12, -1, -7, 8, -15, 30, 16, 28] }],
          varNames: [],
          steps: [
            { highlights: { arr: { primary: [0, 1, 2] } }, note: 'Window [0,2]: scan finds -1 first.' },
            { highlights: { arr: { primary: [1, 2, 3] } }, note: 'Window [1,3]: scan finds -1 first.' },
            { highlights: { arr: { primary: [2, 3, 4] } }, note: 'Window [2,4]: scan finds -7 first.' },
            { highlights: { arr: { primary: [3, 4, 5] } }, note: 'Window [3,5]: scan finds -15 first.' },
            { highlights: { arr: { primary: [4, 5, 6] } }, note: 'Window [4,6]: scan finds -15 first.' },
            { highlights: { arr: { primary: [5, 6, 7] } }, note: 'Window [5,7]: scan finds no negatives — record 0. Results: [-1, -1, -7, -15, -15, 0].' },
          ],
        },
        code:
`public List<Integer> firstNegativeBasic(int[] arr, int k) {
    List<Integer> result = new ArrayList<>();
    for (int start = 0; start <= arr.length - k; start++) {
        int firstNeg = 0;
        for (int i = start; i < start + k; i++) {
            if (arr[i] < 0) { firstNeg = arr[i]; break; }
        }
        result.add(firstNeg);
    }
    return result;
}`,
        complexity: { time: 'O(n · k)', space: 'O(n) — for the result list' },
      },
      {
        name: 'Optimized — A Deque of Negative Indices',
        explanation: 'Keep a deque holding only the indices of negative numbers currently inside the window, oldest first. The front of the deque is always the first negative in the current window — no rescanning needed, just drop indices that have slid out the left.',
        steps: [
          'Walk the array once. Whenever a negative number is seen, append its index to the deque.',
          'Once the window reaches size k, drop any index from the front of the deque that has fallen outside the window.',
          'The front of the deque (if any remain) is the first negative in the current window — read it directly.',
          'Each index enters and leaves the deque at most once — O(n) total, not O(n · k).',
        ],
        visual: {
          arrays: [{ label: 'arr (k = 3)', values: [12, -1, -7, 8, -15, 30, 16, 28] }, { label: 'negIndices (deque)', values: [] }],
          varNames: [],
          steps: [
            { highlights: { arr: [0] }, note: '12 is not negative — deque stays empty.' },
            { highlights: { arr: [1] }, snapshots: { 'negIndices (deque)': [1] }, note: '-1 is negative — append index 1. Window not full yet.' },
            { highlights: { arr: [2] }, snapshots: { 'negIndices (deque)': [1, 2] }, note: '-7 is negative — append index 2. Window [0,2] full: front of deque is index 1 → first negative is -1.' },
            { highlights: { arr: [3] }, snapshots: { 'negIndices (deque)': [1, 2] }, note: '8 is not negative. Window [1,3]: front index 1 still inside window → first negative is -1.' },
            { highlights: { arr: [4] }, snapshots: { 'negIndices (deque)': [2, 4] }, note: '-15 is negative — append index 4. Window [2,4]: front index 1 fell outside (< 2) → drop it. New front is index 2 → first negative is -7.' },
          ],
        },
        code:
`public List<Integer> firstNegative(int[] arr, int k) {
    List<Integer> result = new ArrayList<>();
    Deque<Integer> negIndices = new ArrayDeque<>();

    for (int i = 0; i < arr.length; i++) {
        if (arr[i] < 0) negIndices.addLast(i);

        if (i >= k - 1) {
            while (!negIndices.isEmpty() && negIndices.peekFirst() <= i - k) {
                negIndices.pollFirst();
            }
            result.add(negIndices.isEmpty() ? 0 : arr[negIndices.peekFirst()]);
        }
    }
    return result;
}`,
        complexity: { time: 'O(n)', space: 'O(k) — for the deque' },
      },
    ],
  },
  {
    id: 'smallest-subarray-sum-target',
    title: 'Smallest Subarray With a Sum ≥ Target',
    level: 'Medium',
    versions: [
      {
        name: 'Basic — Check Every Subarray',
        explanation: 'Try every possible subarray, sum it, and check whether it meets the target. Direct, but every subarray gets its own summation even though most overlap heavily with others.',
        steps: [
          'For each start index i, extend a running sum to every end index j ≥ i.',
          'Whenever the running sum reaches the target, record the window length.',
          'Keep the smallest length found across every start.',
          'Every subarray is summed — O(n²) total.',
        ],
        code:
`public int smallestSubarrayBasic(int[] arr, int target) {
    int minLen = Integer.MAX_VALUE;
    for (int i = 0; i < arr.length; i++) {
        int sum = 0;
        for (int j = i; j < arr.length; j++) {
            sum += arr[j];
            if (sum >= target) {
                minLen = Math.min(minLen, j - i + 1);
                break;
            }
        }
    }
    return minLen == Integer.MAX_VALUE ? 0 : minLen;
}`,
        complexity: { time: 'O(n²)', space: 'O(1)' },
      },
      {
        name: 'Optimized — Variable Window That Expands and Shrinks',
        explanation: 'Expand the window from the right until the sum finally meets the target, then shrink from the left as far as possible while it still does — recording the shortest length along the way. Every index enters and leaves the window at most once, so the whole scan is O(n).',
        steps: [
          'Expand right, adding each new element to a running sum.',
          'While the sum meets or exceeds the target, record the window length as a candidate, then shrink from the left (subtracting as you go).',
          'Keep expanding and shrinking until right reaches the end.',
          'The smallest recorded length is the answer.',
        ],
        visual: {
          arrays: [{ label: 'arr (target = 7)', values: [2, 3, 1, 2, 4, 3] }],
          varNames: ['sum', 'minLen'],
          steps: [
            { highlights: { arr: { primary: [0, 1, 2, 3] } }, vars: { sum: 8, minLen: 4 }, note: 'Expanded to index 3: sum = 2+3+1+2 = 8 ≥ 7. Window length 4 recorded.' },
            { highlights: { arr: { primary: [1, 2, 3] } }, vars: { sum: 6, minLen: 4 }, note: 'Shrink: subtract arr[0]=2. sum = 6 < 7 — stop shrinking, keep expanding.' },
            { highlights: { arr: { primary: [1, 2, 3, 4] } }, vars: { sum: 10, minLen: 3 }, note: 'Expand to index 4: sum = 6+4 = 10 ≥ 7. Shrink: subtract arr[1]=3 → sum=7, still ≥7, length 3 recorded. Shrink again: subtract arr[2]=1 → sum=6 < 7, stop.' },
            { highlights: { arr: { primary: [3, 4, 5] } }, vars: { sum: 9, minLen: 2 }, note: 'Expand to index 5: sum = 6+3 = 9 ≥ 7. Shrink: subtract arr[3]=2 → sum=7, still ≥7, length 2 recorded! Shrink again: subtract arr[4]=4 → sum=3 < 7, stop. Final smallest length: 2.' },
          ],
        },
        code:
`public int smallestSubarray(int[] arr, int target) {
    int left = 0, sum = 0, minLen = Integer.MAX_VALUE;
    for (int right = 0; right < arr.length; right++) {
        sum += arr[right];
        while (sum >= target) {
            minLen = Math.min(minLen, right - left + 1);
            sum -= arr[left];
            left++;
        }
    }
    return minLen == Integer.MAX_VALUE ? 0 : minLen;
}`,
        complexity: { time: 'O(n)', space: 'O(1)' },
      },
    ],
  },
  {
    id: 'longest-substring-k-distinct',
    title: 'Longest Substring With At Most K Distinct Characters',
    level: 'Medium',
    crossReference:
      "This drill's full Basic vs. Optimized walkthrough lives in this pattern's curated problem list — open " +
      "\"Longest Substring with At Most K Distinct Characters\" there for the complete step-by-step and both " +
      "code versions. You can also try it hands-on in the \"K Distinct Characters\" mode of the Interactive " +
      "Practice playground with any string and k of your choosing.",
  },
  {
    id: 'max-consecutive-ones-k-flips',
    title: 'Max Consecutive Ones After Flipping At Most K Zeros',
    level: 'Medium',
    versions: [
      {
        name: 'Basic — Check Every Window, Count Its Zeros',
        explanation: 'For every possible window, count how many zeros it contains by scanning it directly. If that count is within the flip budget, the window is valid — track the longest valid one. Every window gets its own O(n) scan.',
        steps: [
          'For each window start and end, count the zeros inside it by scanning.',
          'If the zero count is ≤ k, the window is valid — track the longest valid length.',
          'Every window is scanned independently — O(n²) or worse depending on how windows are generated.',
        ],
        code:
`public int maxConsecutiveOnesBasic(int[] arr, int k) {
    int maxLen = 0;
    for (int i = 0; i < arr.length; i++) {
        int zeros = 0;
        for (int j = i; j < arr.length; j++) {
            if (arr[j] == 0) zeros++;
            if (zeros > k) break;
            maxLen = Math.max(maxLen, j - i + 1);
        }
    }
    return maxLen;
}`,
        complexity: { time: 'O(n²)', space: 'O(1)' },
      },
      {
        name: 'Optimized — Variable Window With a Zero Counter',
        explanation: 'Expand the window, incrementing a zero counter whenever a 0 enters. The moment the counter exceeds the flip budget k, shrink from the left until it\'s back within budget. The window\'s length at every valid moment is a candidate for the answer.',
        steps: [
          'Expand right one element at a time; if it\'s a 0, increment the zero counter.',
          'While the zero counter exceeds k, shrink from the left (decrementing the counter if the outgoing element was a 0).',
          'After each expansion (and any needed shrinking), the current window is always valid — track its length as the max.',
          'Every index enters and leaves the window at most once — O(n).',
        ],
        visual: {
          arrays: [{ label: 'arr (k = 1)', values: [1, 0, 0, 1, 1, 0, 1] }],
          varNames: ['zeros', 'maxLen'],
          steps: [
            { highlights: { arr: { primary: [0, 1] } }, vars: { zeros: 1, maxLen: 2 }, note: 'right=1 (a 0): zeros becomes 1, still within budget (k=1). Window [0,1], length 2.' },
            { highlights: { arr: { primary: [2] } }, vars: { zeros: 1, maxLen: 2 }, note: 'right=2 (another 0): zeros becomes 2, over budget. Shrink: drop index 0 (a 1, zeros stays 2), drop index 1 (a 0, zeros drops to 1) — stop. Window is now just [2,2].' },
            { highlights: { arr: { primary: [2, 3, 4] } }, vars: { zeros: 1, maxLen: 3 }, note: 'Expand through indices 3 and 4 (both 1s) — zeros stays 1, no shrinking needed. Window [2,4], length 3 — new max.' },
            { highlights: { arr: { primary: [3, 4, 5] } }, vars: { zeros: 1, maxLen: 3 }, note: 'right=5 (a 0): zeros becomes 2, over budget. Shrink: drop index 2 (a 0, zeros drops to 1) — stop. Window is now [3,5], length 3 — not better.' },
            { highlights: { arr: { primary: [3, 4, 5, 6] } }, vars: { zeros: 1, maxLen: 4 }, note: 'right=6 (a 1): zeros stays 1, still within budget. Window [3,6], length 4 — new max! Final answer: 4.' },
          ],
        },
        code:
`public int maxConsecutiveOnes(int[] arr, int k) {
    int left = 0, zeros = 0, maxLen = 0;
    for (int right = 0; right < arr.length; right++) {
        if (arr[right] == 0) zeros++;
        while (zeros > k) {
            if (arr[left] == 0) zeros--;
            left++;
        }
        maxLen = Math.max(maxLen, right - left + 1);
    }
    return maxLen;
}`,
        complexity: { time: 'O(n)', space: 'O(1)' },
      },
    ],
  },
  {
    id: 'longest-subarray-sum-exactly-k',
    title: 'Longest Subarray With Sum Exactly K (Non-Negative Numbers)',
    level: 'Advanced',
    versions: [
      {
        name: 'Basic — Check Every Subarray\'s Sum',
        explanation: 'Try every subarray, sum it directly, and check if it equals k exactly. Direct, but every subarray is summed independently even though the array only contains non-negative numbers — a fact this approach never exploits.',
        steps: [
          'For each start index, extend a running sum to every end index.',
          'Whenever the sum equals k exactly, record the window length as a candidate.',
          'Track the longest such length across every start and end pair.',
          'Every subarray sum is computed independently — O(n²).',
        ],
        code:
`public int longestSubarraySumBasic(int[] arr, int k) {
    int maxLen = 0;
    for (int i = 0; i < arr.length; i++) {
        int sum = 0;
        for (int j = i; j < arr.length; j++) {
            sum += arr[j];
            if (sum == k) {
                maxLen = Math.max(maxLen, j - i + 1);
            }
        }
    }
    return maxLen;
}`,
        complexity: { time: 'O(n²)', space: 'O(1)' },
      },
      {
        name: 'Optimized — Sliding Window (Valid Because Every Value Is Non-Negative)',
        explanation: 'Because every element is non-negative, growing the window can only ever increase (or hold steady) the sum — so once the sum exceeds k, shrinking from the left is the only sensible move, and it\'s never wrong to do so. That monotonic behavior is exactly what makes a sliding window valid here — it would break instantly with negative numbers.',
        steps: [
          'Expand right, adding each element to the running sum.',
          'While the sum exceeds k, shrink from the left.',
          'Whenever the sum equals k exactly, record the window length as a candidate for the longest.',
          'Every index enters and leaves the window at most once — O(n).',
        ],
        visual: {
          arrays: [{ label: 'arr (k = 4)', values: [1, 2, 1, 1, 3] }],
          varNames: ['sum', 'maxLen'],
          steps: [
            { highlights: { arr: { primary: [0, 1, 2] } }, vars: { sum: 4, maxLen: 3 }, note: 'Expanded to index 2: sum = 1+2+1 = 4, exactly k! Window length 3 recorded.' },
            { highlights: { arr: { primary: [1, 2, 3] } }, vars: { sum: 4, maxLen: 3 }, note: 'Expand to index 3: sum = 5, over k — shrink: subtract arr[0]=1, sum=4. Exactly k again, but length 3 — not better.' },
            { highlights: { arr: { primary: [2, 3, 4] } }, vars: { sum: 4, maxLen: 3 }, note: 'Expand to index 4: sum = 7, over k — shrink twice: subtract arr[1]=2 (sum=5), subtract arr[2]=1 (sum=4). Exactly k again, window [3,4], length 2 — not better than 3. Final answer: 3.' },
          ],
        },
        code:
`public int longestSubarraySum(int[] arr, int k) {
    int left = 0, sum = 0, maxLen = 0;
    for (int right = 0; right < arr.length; right++) {
        sum += arr[right];
        while (sum > k) {
            sum -= arr[left];
            left++;
        }
        if (sum == k) {
            maxLen = Math.max(maxLen, right - left + 1);
        }
    }
    return maxLen;
}`,
        complexity: { time: 'O(n)', space: 'O(1)' },
      },
    ],
  },
  {
    id: 'count-anagram-substrings',
    title: 'Count Anagram Substrings (Permutation in String Foundation)',
    level: 'Advanced',
    crossReference:
      "This drill's full Basic vs. Optimized walkthrough lives in this pattern's curated problem list — open " +
      "\"Permutation in String\" there for the complete fixed-window frequency-matching technique and both code " +
      "versions. It's the exact same fixed-size-window idea from the Maximum Sum Subarray drill, combined with " +
      "the frequency-map comparison from the Hashing pattern group.",
  },
  {
    id: 'subarray-product-less-than-k',
    title: 'Longest Subarray With Product Less Than K',
    level: 'Advanced',
    versions: [
      {
        name: 'Basic — Check Every Subarray\'s Product',
        explanation: 'Try every subarray, multiply its elements directly, and check whether the product stays under k. Direct, but the same overlapping-work problem as every brute-force subarray scan.',
        steps: [
          'For each start index, extend a running product to every end index.',
          'The moment the product reaches or exceeds k, stop extending from this start (it can only grow further).',
          'Track the longest valid window length found.',
          'Every subarray\'s product is computed independently — O(n²).',
        ],
        code:
`public int longestProductLessThanKBasic(int[] arr, int k) {
    int maxLen = 0;
    for (int i = 0; i < arr.length; i++) {
        long product = 1;
        for (int j = i; j < arr.length; j++) {
            product *= arr[j];
            if (product >= k) break;
            maxLen = Math.max(maxLen, j - i + 1);
        }
    }
    return maxLen;
}`,
        complexity: { time: 'O(n²)', space: 'O(1)' },
      },
      {
        name: 'Optimized — Variable Window, Divide Out on Shrink',
        explanation: 'Expand the window, multiplying the running product by each new element. The moment the product reaches or exceeds k, shrink from the left, DIVIDING the outgoing element back out — exactly mirroring how a sum-based window subtracts on shrink, just with multiplication and division instead of addition and subtraction.',
        steps: [
          'Expand right, multiplying the running product by each new element.',
          'While the product is ≥ k, shrink from the left, dividing the outgoing element back out of the product.',
          'After any needed shrinking, the current window is always valid — track its length as the max.',
          'Every index enters and leaves the window at most once — O(n).',
        ],
        visual: {
          arrays: [{ label: 'arr (k = 100)', values: [10, 5, 2, 6] }],
          varNames: ['product', 'maxLen'],
          steps: [
            { highlights: { arr: { primary: [0] } }, vars: { product: 10, maxLen: 1 }, note: 'Expand to index 0: product = 10 < 100. Length 1.' },
            { highlights: { arr: { primary: [0, 1] } }, vars: { product: 50, maxLen: 2 }, note: 'Expand to index 1: product = 10×5 = 50 < 100. Length 2.' },
            { highlights: { arr: { primary: [1, 2] } }, vars: { product: 10, maxLen: 2 }, note: 'Expand to index 2: product = 50×2 = 100, ≥ 100 — shrink: divide out arr[0]=10 → product=10. Window is now [1,2], length 2 — not better.' },
            { highlights: { arr: { primary: [1, 2, 3] } }, vars: { product: 60, maxLen: 3 }, note: 'Expand to index 3: product = 10×6 = 60 < 100. Window [1,3], length 3 — new max! Final answer: 3.' },
          ],
        },
        code:
`public int longestProductLessThanK(int[] arr, int k) {
    if (k <= 1) return 0;
    int left = 0, maxLen = 0;
    long product = 1;
    for (int right = 0; right < arr.length; right++) {
        product *= arr[right];
        while (product >= k) {
            product /= arr[left];
            left++;
        }
        maxLen = Math.max(maxLen, right - left + 1);
    }
    return maxLen;
}`,
        complexity: { time: 'O(n)', space: 'O(1)' },
      },
    ],
  },
];

export default slidingWindowDrills;
